import React, { useMemo, useState } from 'react';

import { css, StyleSheet } from 'aphrodite';
import PropTypes from 'prop-types';
import { Button } from 'react-bootstrap';
import { useHistory, useParams } from 'react-router-dom';

import MfrBrandsPage from 'components/upb_mfr_viewer/MfrBrandsPage';
import MfrManufacturersPage from 'components/upb_mfr_viewer/MfrManufacturersPage';
import MfrProductsPage from 'components/upb_mfr_viewer/MfrProductsPage';
import MfrSearchResults from 'components/upb_mfr_viewer/MfrSearchResults';
import BetaSegmentedToggle from 'components/upb_viewer_beta/BetaSegmentedToggle';
import useMfrCategoryGmv from 'components/upb_mfr_viewer/hooks/useMfrCategoryGmv';
import useMfrKpiMetrics from 'components/upb_mfr_viewer/hooks/useMfrKpiMetrics';
import {
  constructUrlPathSegment,
  getCategoryIDFromUrl,
  getCategoryNameFromUrl,
} from 'components/upb_viewer/UPBViewer';
import SupportTicketsCreateModal from 'components/upb_viewer/SupportTicketsCreateModal';
import BetaBreadcrumb from 'components/upb_viewer_beta/BetaBreadcrumb';
import BetaDistributorFilter from 'components/upb_viewer_beta/BetaDistributorFilter';
import BetaKpiStrip from 'components/upb_viewer_beta/BetaKpiStrip';
import BetaL1Sidebar from 'components/upb_viewer_beta/BetaL1Sidebar';
import BetaTopBar from 'components/upb_viewer_beta/BetaTopBar';
import {
  BG_1,
  BG_2,
  BORDER_2,
  FG_1,
  FG_2,
  FONT_BODY,
  FONT_TITLE,
  FONT_WEIGHT_EXTRABOLD,
  GLOBAL_NAV_HEIGHT,
  KPI_STRIP_HEIGHT,
  PRIMARY_COLOR,
  PRIMARY_HOVER,
  RADIUS_LG,
  SHADOW_XS,
  SIDEBAR_CARD_WIDTH,
  TOPBAR_HEIGHT,
} from 'components/upb_viewer_beta/css/betaTokens';
import useGet from 'lib/hooks/useGet';
import { TAXONOMY_CATEGORY_LEVEL_OPTIONS_WITH_SUB_OPTIONS } from 'lib/networking/endpoints';

const BASE_URL = 'upb-mfr-viewer-beta';

// Search result modes the user can toggle between.
const SEARCH_VIEWS = {
  PRODUCTS: 'products',
  MANUFACTURERS: 'manufacturers',
};
const SEARCH_VIEW_OPTIONS = [
  { value: SEARCH_VIEWS.PRODUCTS, label: 'Products' },
  { value: SEARCH_VIEWS.MANUFACTURERS, label: 'Manufacturers' },
];

const KPI_KEYS = {
  ANNUALIZED_GMV: 'annualized_gmv',
  MANUFACTURERS: 'manufacturers',
  CUSTOMERS: 'customers',
  TOTAL_SKUS: 'total_skus',
  ACTIVE_SKUS: 'active_skus',
  GMV_SKUS: 'gmv_skus',
  AVG_ECOM_SCORE: 'avg_ecom_score',
  AVG_ATTRIBUTE_SCORE: 'avg_attribute_score',
};

/** % to one decimal (org formatting rule); em-dash when missing. */
const fmtPct = v => (v != null ? `${Number(v).toFixed(1)}%` : '—');
/** Currency with commas; em-dash when missing/zero. */
const fmtMoney = v => (v ? `$${Math.round(v).toLocaleString('en-US')}` : '—');
/** Integer count with commas; em-dash when missing (0 shows as 0). */
const fmtCount = v => (v != null ? Number(v).toLocaleString('en-US') : '—');

/** Root (manufacturers list) KPI cards from the kpi-metrics payload. */
function buildMfrMetrics(data) {
  return [
    {
      key: KPI_KEYS.ANNUALIZED_GMV,
      label: 'Annualized GMV',
      value: fmtMoney(data?.annualized_gmv),
      tooltip: 'Total annualized GMV (L30d)',
    },
    {
      key: KPI_KEYS.MANUFACTURERS,
      label: 'Manufacturers',
      value: fmtCount(data?.manufacturer_count),
      tooltip: 'Manufacturers with GMV in L30d',
    },
    {
      key: KPI_KEYS.AVG_ECOM_SCORE,
      label: 'Avg eCom Score',
      value: fmtPct(data?.avg_ecom_score),
      tooltip: 'Average eCom score for active products',
    },
    {
      key: KPI_KEYS.AVG_ATTRIBUTE_SCORE,
      label: 'Avg Attribute Score',
      value: fmtPct(data?.avg_attribute_score),
      tooltip: 'Average attribute score across active SKUs',
    },
  ];
}

/**
 * Detail KPI cards shown once you've drilled into a manufacturer (or brand) —
 * the Tyson-mockup tile set, scoped to that manufacturer/brand.
 */
function buildMfrDetailMetrics(data) {
  return [
    {
      key: KPI_KEYS.ANNUALIZED_GMV,
      label: 'Annualized GMV',
      value: fmtMoney(data?.annualized_gmv),
      tooltip: 'Annualized GMV for this manufacturer/brand (based on L30D)',
    },
    {
      key: KPI_KEYS.CUSTOMERS,
      label: 'Customers',
      value: fmtCount(data?.customers),
      tooltip: 'Operators who purchased this brand in L30D',
    },
    {
      key: KPI_KEYS.TOTAL_SKUS,
      label: 'Total SKUs',
      value: fmtCount(data?.total_skus),
      tooltip: 'All distributor SKUs for this manufacturer/brand',
    },
    {
      key: KPI_KEYS.ACTIVE_SKUS,
      label: 'Active SKUs',
      value: fmtCount(data?.active_skus),
      tooltip: 'SKUs marked active by a distributor',
    },
    {
      key: KPI_KEYS.GMV_SKUS,
      label: 'GMV SKUs',
      value: fmtCount(data?.gmv_skus),
      tooltip: 'SKUs with GMV in L30D',
    },
    {
      key: KPI_KEYS.AVG_ECOM_SCORE,
      label: 'Avg eCom Score',
      value: fmtPct(data?.avg_ecom_score),
      tooltip: 'Average eCom score for active products',
    },
    {
      key: KPI_KEYS.AVG_ATTRIBUTE_SCORE,
      label: 'Avg Attribute Score',
      value: fmtPct(data?.avg_attribute_score),
      tooltip: 'Average attribute score across active SKUs',
    },
  ];
}

/**
 * MfrViewerContainer — Manufacturer Sales Viewer shell. Same chrome as the UPB
 * Viewer Beta (top bar + KPI strip + category sidebar), but the body browses
 * manufacturers → brands → products. The sidebar category selection and the
 * KPI-strip distributor filter scope the manufacturer/brand grids and the KPIs.
 *
 * Routes:
 *   /upb-mfr-viewer-beta                          → manufacturers
 *   /upb-mfr-viewer-beta/:manufacturer_id         → that manufacturer's brands
 *   /upb-mfr-viewer-beta/:manufacturer_id/:brand_id → that brand's products
 * (each id segment is `Name__id`, built with constructUrlPathSegment.)
 */
function MfrViewerContainer({ navExpanded }) {
  const history = useHistory();
  const { manufacturer_id, brand_id } = useParams();

  const [activeMetric, setActiveMetric] = useState(KPI_KEYS.ANNUALIZED_GMV);
  const [searchQuery, setSearchQuery] = useState('');
  // Which result set search shows: relevant products (default) or the
  // manufacturers ranked by their products' relevance. User-selectable toggle.
  const [searchView, setSearchView] = useState(SEARCH_VIEWS.PRODUCTS);
  const [vendorId, setVendorId] = useState(null);
  // Distributor-group filter (Influence campaign `third_party` tag); mutually
  // exclusive with a single vendorId.
  const [thirdPartyTag, setThirdPartyTag] = useState(null);
  const [selectedL1Id, setSelectedL1Id] = useState(null);
  const [selectedL2Id, setSelectedL2Id] = useState(null);
  const [expandedL1Id, setExpandedL1Id] = useState(null);
  // "Report an issue" ticket modal, opened from the header card.
  const [showIssueModal, setShowIssueModal] = useState(false);

  const mfrId = manufacturer_id ? Number(getCategoryIDFromUrl(manufacturer_id)) : null;
  const brandId = brand_id ? Number(getCategoryIDFromUrl(brand_id)) : null;
  const mfrName = getCategoryNameFromUrl(manufacturer_id) || '';
  const brandName = getCategoryNameFromUrl(brand_id) || '';

  // Re-encode the manufacturer URL segment (useParams returns it decoded, e.g.
  // "Tyson Foods__2" with a space). Use the same encoder the cards use so the
  // breadcrumb/back links produce valid encoded paths (e.g. "Tyson%20Foods__2").
  const manufacturerSeg = mfrId ? constructUrlPathSegment({ value: mfrId, label: mfrName }) : null;

  // Once drilled into a manufacturer, the reporter's MFC is unambiguous, so the
  // ticket modal takes it directly (mfrId is a global_manufacturer id) and skips
  // the distributor picker — same treatment as the beta PDP. On the root
  // manufacturers list and in search there's no such context, so the modal falls
  // back to asking for a distributor/manufacturer.
  const ticketManufacturer = useMemo(
    () => (mfrId ? { label: mfrName || 'Manufacturer', value: mfrId } : null),
    [mfrId, mfrName],
  );

  const handleDistributorChange = ({ vendorId: nextVendorId, thirdPartyTag: nextTag }) => {
    setVendorId(nextVendorId);
    setThirdPartyTag(nextTag);
  };

  const trimmedSearch = (searchQuery || '').trim();

  // L1 categories — sidebar tree (shared taxonomy endpoint).
  const { data: taxonomyData, loading: categoriesLoading } = useGet(
    TAXONOMY_CATEGORY_LEVEL_OPTIONS_WITH_SUB_OPTIONS,
    { is_upb: true },
  );
  const categories = taxonomyData?.data || [];

  // Per-L1 GMV for the sidebar — branded MFR GMV (same basis as the KPI), so the
  // "All Categories" total reconciles with the Annualized GMV headline. Rows with
  // no L4→L1 mapping arrive as CAT_VALUE = -1; they're summed into the total but
  // ignored for per-L1 display (no category has id -1).
  const { data: l1GmvData } = useMfrCategoryGmv({
    distributorId: vendorId || undefined,
    thirdPartyTag: thirdPartyTag || undefined,
    manufacturerId: mfrId || undefined,
    brandId: brandId || undefined,
  });
  const { gmvByL1Id, totalGmv } = useMemo(() => {
    const rows = l1GmvData?.data || [];
    const map = {};
    let total = 0;
    rows.forEach(row => {
      const gmv = Number(row.MONITIZABLE_GMV) || 0;
      map[Number(row.CAT_VALUE)] = gmv;
      total += gmv;
    });
    return { gmvByL1Id: map, totalGmv: total };
  }, [l1GmvData]);

  // Per-L2 GMV for the expanded L1 (lazy), same branded MFR basis.
  const { data: l2GmvData } = useMfrCategoryGmv({
    l1CategoryId: expandedL1Id || undefined,
    distributorId: vendorId || undefined,
    thirdPartyTag: thirdPartyTag || undefined,
    manufacturerId: mfrId || undefined,
    brandId: brandId || undefined,
    skip: !expandedL1Id,
  });
  const gmvByL2Id = useMemo(() => {
    const rows = l2GmvData?.data || [];
    const map = {};
    rows.forEach(row => {
      if (row && row.CAT_VALUE != null) {
        map[Number(row.CAT_VALUE)] = Number(row.MONITIZABLE_GMV) || 0;
      }
    });
    return map;
  }, [l2GmvData]);

  // KPI strip — scoped by the deepest category filter + distributor, and by the
  // drilled-in manufacturer/brand. Once a manufacturer is selected the strip
  // switches to the richer per-manufacturer tile set (Tyson mockup).
  const isDetail = !!mfrId;
  const { data: kpiData, loading: kpiLoading } = useMfrKpiMetrics({
    l1CategoryId: selectedL1Id || undefined,
    l2CategoryId: selectedL2Id || undefined,
    distributorId: vendorId || undefined,
    thirdPartyTag: thirdPartyTag || undefined,
    manufacturerId: mfrId || undefined,
    brandId: brandId || undefined,
  });
  const metrics = isDetail ? buildMfrDetailMetrics(kpiData) : buildMfrMetrics(kpiData);
  // Keep the highlighted tile valid across tile-set changes (Annualized GMV is
  // in both sets; fall back to it if the active key isn't in the current set).
  const safeActiveMetric = metrics.some(m => m.key === activeMetric)
    ? activeMetric
    : KPI_KEYS.ANNUALIZED_GMV;

  // Sidebar drives grid FILTERS (not navigation). BetaL1Sidebar calls onSelect
  // for the L1/All rows and onNavigate for an L2 row (path
  // `/baseURL/<L1seg>/<L2seg>`); we translate that path back into filter ids.
  // Clicking a sidebar category exits search: clear the term so we return to the
  // normal (category-filtered) view. Clearing searchQuery also clears the search
  // box (the top bar's draft syncs from it).
  const handleSelectL1 = id => {
    setSearchQuery('');
    setSelectedL1Id(id);
    setSelectedL2Id(null);
  };
  const handleSidebarNavigate = path => {
    const segs = String(path).split('/').filter(Boolean); // [baseURL, l1seg, l2seg?]
    const l1 = segs[1] ? Number(getCategoryIDFromUrl(segs[1])) : null;
    const l2 = segs[2] ? Number(getCategoryIDFromUrl(segs[2])) : null;
    setSearchQuery('');
    setSelectedL1Id(l1 || null);
    setSelectedL2Id(l2 || null);
  };
  // Drilling into a manufacturer from the search results exits search (so we land
  // on that manufacturer's brands, not back on the search view).
  const handleNavigateClearSearch = url => {
    setSearchQuery('');
    history.push(url);
  };
  const handleToggleExpand = id => {
    setExpandedL1Id(prev => (prev === id ? null : id));
  };

  // Content header card (per mock): breadcrumb + page title on the left, the
  // distributor filter on the right. Lives in the content column — NOT the KPI
  // strip — so the strip is pure metric tiles.
  const isSearching = trimmedSearch.length > 0;
  let headerCrumbs = null;
  let headerTitle = 'Manufacturers';
  if (isSearching) {
    headerCrumbs = [
      { label: 'Manufacturers', url: `/${BASE_URL}` },
      { label: `Search: "${trimmedSearch}"` },
    ];
    headerTitle = 'Search results';
  } else if (mfrId && brandId) {
    headerCrumbs = [
      { label: 'Manufacturers', url: `/${BASE_URL}` },
      { label: mfrName || 'Manufacturer', url: `/${BASE_URL}/${manufacturerSeg}` },
      { label: brandName || 'Brand' },
    ];
    headerTitle = brandName || 'Products';
  } else if (mfrId) {
    headerCrumbs = [
      { label: 'Manufacturers', url: `/${BASE_URL}` },
      { label: mfrName || 'Manufacturer' },
    ];
    headerTitle = mfrName || 'Brands';
  }

  const renderBody = () => {
    // Search offers two user-selectable result sets (per product feedback):
    //   - Products: a flat, relevance-ranked product list (Brand / MFC per card).
    //   - Manufacturers: manufacturers ranked by their products' relevance.
    if (isSearching) {
      // The Products/Manufacturers toggle lives next to the search box (top bar).
      return searchView === SEARCH_VIEWS.MANUFACTURERS ? (
        <MfrManufacturersPage
          searchQuery={trimmedSearch}
          distributorId={vendorId}
          onNavigate={handleNavigateClearSearch}
          baseURL={BASE_URL}
        />
      ) : (
        <MfrSearchResults searchQuery={trimmedSearch} />
      );
    }
    if (mfrId && brandId) {
      return <MfrProductsPage brandId={brandId} searchQuery={trimmedSearch} />;
    }
    if (mfrId) {
      return (
        <MfrBrandsPage
          manufacturerId={mfrId}
          manufacturerSeg={manufacturerSeg}
          searchQuery={trimmedSearch}
          l1CategoryId={selectedL1Id}
          l2CategoryId={selectedL2Id}
          distributorId={vendorId}
          thirdPartyTag={thirdPartyTag}
          onNavigate={history.push}
          baseURL={BASE_URL}
        />
      );
    }
    return (
      <MfrManufacturersPage
        searchQuery={trimmedSearch}
        l1CategoryId={selectedL1Id}
        l2CategoryId={selectedL2Id}
        distributorId={vendorId}
        thirdPartyTag={thirdPartyTag}
        onNavigate={history.push}
        baseURL={BASE_URL}
      />
    );
  };

  return (
    <div className={css(styles.pageWrapper)}>
      <BetaTopBar
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        navExpanded={navExpanded}
        acronym="MSV"
        title="Manufacturer Sales Viewer"
        searchAccessory={
          isSearching ? (
            <BetaSegmentedToggle
              value={searchView}
              onChange={setSearchView}
              options={SEARCH_VIEW_OPTIONS}
            />
          ) : null
        }
      />

      <BetaKpiStrip
        metrics={metrics}
        activeMetric={safeActiveMetric}
        onMetricChange={setActiveMetric}
        loading={kpiLoading}
        navExpanded={navExpanded}
      />

      <div className={css(styles.contentGrid)}>
        <BetaL1Sidebar
          categories={categories}
          loading={categoriesLoading}
          selectedL1Id={selectedL1Id}
          selectedL2Id={selectedL2Id}
          expandedL1Id={expandedL1Id}
          onSelect={handleSelectL1}
          onToggleExpand={handleToggleExpand}
          onNavigate={handleSidebarNavigate}
          baseURL={BASE_URL}
          gmvByL1Id={gmvByL1Id}
          gmvByL2Id={gmvByL2Id}
          totalGmv={totalGmv}
        />
        <div className={css(styles.bodyCol)}>
          <div className={css(styles.headerCard)}>
            {/* Distributor dropdown at the UPPER LEFT of the content column,
                mirroring the UPB beta viewer where it is the leading (leftmost)
                item of the fixed chrome sitting above the breadcrumb. The
                "Report an issue" button was deliberately placed next to the
                dropdown, so the two move together as one unit. */}
            <div className={css(styles.headerControls)}>
              <BetaDistributorFilter
                value={vendorId}
                thirdPartyTag={thirdPartyTag}
                onChange={handleDistributorChange}
              />
              <Button
                type="button"
                variant="link"
                className={css(styles.reportIssue)}
                onClick={() => setShowIssueModal(true)}
              >
                <span aria-hidden>⚠</span>
                Report an issue
              </Button>
            </div>
            <div className={css(styles.headerLeft)}>
              {headerCrumbs && <BetaBreadcrumb crumbs={headerCrumbs} />}
              <h1 className={css(styles.headerTitle)}>{headerTitle}</h1>
            </div>
          </div>
          {renderBody()}
        </div>
      </div>

      {showIssueModal && (
        <SupportTicketsCreateModal
          onHide={() => setShowIssueModal(false)}
          refetch={() => {}}
          product={null}
          typeOfProduct={null}
          hideDistributorSelect={!!ticketManufacturer}
          presetManufacturer={ticketManufacturer}
        />
      )}
    </div>
  );
}

const FIXED_CHROME = TOPBAR_HEIGHT + KPI_STRIP_HEIGHT;

const styles = StyleSheet.create({
  pageWrapper: {
    backgroundColor: BG_2,
    fontFamily: FONT_BODY,
    minHeight: `calc(100vh - ${GLOBAL_NAV_HEIGHT}px)`,
  },

  contentGrid: {
    display: 'grid',
    gridTemplateColumns: `${SIDEBAR_CARD_WIDTH}px 1fr`,
    gap: 20,
    padding: '20px 24px 40px',
    marginTop: FIXED_CHROME,
    overflowY: 'auto',
    height: `calc(100vh - ${GLOBAL_NAV_HEIGHT + FIXED_CHROME}px)`,
  },

  bodyCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    minWidth: 0,
  },

  // Content header: white card stacking the DISTRIBUTOR dropdown (+ the report
  // affordance beside it) at the top left, with the breadcrumb and big page
  // title beneath — same top-left-then-breadcrumb order as the UPB beta viewer.
  headerCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 14,
    backgroundColor: BG_1,
    border: `1px solid ${BORDER_2}`,
    borderRadius: RADIUS_LG,
    boxShadow: SHADOW_XS,
    padding: '16px 24px',
  },

  headerLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    minWidth: 0,
  },

  // Distributor dropdown + "Report an issue", kept adjacent as one unit.
  headerControls: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
  },

  // Pill button matching the beta PDP's "Report a Data Issue" affordance.
  reportIssue: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    fontFamily: FONT_BODY,
    fontSize: 13,
    fontWeight: 700,
    color: FG_2,
    padding: '7px 14px',
    borderRadius: 9999,
    backgroundColor: BG_1,
    border: `1px solid ${BORDER_2}`,
    boxShadow: SHADOW_XS,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    textDecoration: 'none',
    ':hover': {
      color: PRIMARY_HOVER,
      borderColor: PRIMARY_COLOR,
      textDecoration: 'none',
    },
  },

  headerTitle: {
    fontFamily: FONT_TITLE,
    fontWeight: FONT_WEIGHT_EXTRABOLD,
    fontSize: 24,
    color: FG_1,
    letterSpacing: '-0.01em',
    margin: 0,
  },
});

MfrViewerContainer.propTypes = {
  // True when the global navigation rail is expanded; shifts the fixed top bar +
  // KPI strip right by the rail width so they don't overlap (mirrors the beta).
  navExpanded: PropTypes.bool,
};

export default MfrViewerContainer;
